import {linearToSrgb} from "@cm/image-processing/operations/tone-mapping"
import {Box2Like} from "@cm/math"
import {AnyImageOpResultNode, NativeImageSource} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {ImageOpNodeDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/node"
import {ImageOpNodeExtractRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/node"
import {ImageOpSystem} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-system"
import {computeLabStats} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/lab-stats-subgraph"
import {getBakedMapDataObjectIds} from "@app/textures/texture-editor/texture-editor-callback"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {descriptorByTextureType} from "@app/textures/utils/texture-type-descriptor"
import {decodeTiffToImageData} from "@common/helpers/utils/decode-tiff"
import {DataObjectThumbnailService} from "@common/services/data-object-thumbnail/data-object-thumbnail.service"
import {DownloadResolution, TextureType} from "@generated"

const MAX_PREVIEW_SIZE = 640

export type RecolorReferenceTextureMap = {
    textureSetRevisionId: string
    textureType: TextureType
}

// An image the recolor operator can compute its target statistics from: either an uploaded file or
// an existing texture set revision's map. Everything downstream of the loaders below (statistics,
// region selection) works on this and doesn't care which of the two it is.
export type RecolorReferenceImage = {
    // full resolution
    readonly width: number
    readonly height: number
    readonly channels: number
    readonly displayName: string
    readonly previewUrl: string | undefined
    readonly textureMap?: RecolorReferenceTextureMap // unset for uploads
    createNode(bag: ImageOpNodeBag, id: string): AnyImageOpResultNode
    dispose(): void
}

export async function loadReferenceImageFromFile(file: File, texturesApi: TexturesApiService): Promise<RecolorReferenceImage> {
    const fileName = file.name.toLowerCase()
    if (fileName.endsWith(".exr")) {
        // decodeEXR runs entirely client-side (WASM worker), no backend round trip
        const decoded = await texturesApi.decodeEXR(await file.arrayBuffer())
        if (decoded.dataType !== "float32") {
            throw new Error(`Unsupported EXR data type: ${decoded.dataType}`)
        }
        // decoded.data comes back as a Uint8Array view over the underlying float32 bytes (same as
        // image-op-data-object-page-supplier-full-image.ts's convertToImageData), not an actual
        // Float32Array instance — reinterpret the same buffer instead of casting it.
        const data = new Float32Array(
            decoded.data.buffer,
            decoded.data.byteOffset,
            decoded.data.byteLength / Float32Array.BYTES_PER_ELEMENT,
        ) as Float32Array<ArrayBuffer>
        const descriptor = {width: decoded.width, height: decoded.height, channels: decoded.channelLayout.length}
        const previewUrl = await createPreviewUrlFromPixelData({...descriptor, data})
        return {
            ...descriptor,
            displayName: file.name,
            previewUrl,
            createNode: (bag, id) => bag.getNode(id, ImageOpNodeCreateImage, {descriptor, data}),
            dispose: () => URL.revokeObjectURL(previewUrl),
        }
    }

    // Browsers don't reliably decode TIFF via createImageBitmap(file), so decode it ourselves
    // (same client-side, no-backend-round-trip approach as EXR above) and hand off an ImageData
    // instead. No color space info is read from the TIFF - it's assumed to be sRGB, same as any
    // other non-EXR format here.
    const bitmap =
        fileName.endsWith(".tif") || fileName.endsWith(".tiff")
            ? await createImageBitmap(decodeTiffToImageData(new Uint8Array(await file.arrayBuffer())))
            : await createImageBitmap(file)
    let previewUrl: string
    try {
        previewUrl = await createPreviewUrlFromBitmap(bitmap)
    } catch (error) {
        bitmap.close()
        throw error
    }
    const descriptor = {width: bitmap.width, height: bitmap.height, channels: 4}
    const data = new NativeImageSource(true, bitmap)
    return {
        ...descriptor,
        displayName: file.name,
        previewUrl,
        createNode: (bag, id) => bag.getNode(id, ImageOpNodeCreateImage, {descriptor, data}),
        dispose: () => {
            bitmap.close()
            URL.revokeObjectURL(previewUrl)
        },
    }
}

export async function loadReferenceImageFromTextureMap(
    textureMap: RecolorReferenceTextureMap,
    texturesApi: TexturesApiService,
    thumbnails: DataObjectThumbnailService,
): Promise<RecolorReferenceImage> {
    // this query ignores errors, so a revision that no longer exists comes back as null despite its type
    const revision = await texturesApi.queryTextureSetRevision(textureMap.textureSetRevisionId)
    if (!revision) {
        throw new Error(`Texture set revision ${textureMap.textureSetRevisionId} is not available`)
    }
    const dataObjectId = getBakedMapDataObjectIds(revision).get(textureMap.textureType)
    if (!dataObjectId) {
        throw new Error(`Texture set revision ${textureMap.textureSetRevisionId} has no ${textureMap.textureType} map`)
    }
    const [{width, height}, thumbnail] = await Promise.all([
        texturesApi.queryDataObjectImageDescriptor(dataObjectId),
        // the preview is only needed for selecting a region, the statistics work without it
        thumbnails.fetch({id: dataObjectId, resolution: DownloadResolution.Medium}).catch(() => undefined),
    ])
    const {label, channelLayout} = descriptorByTextureType(textureMap.textureType)
    const channels = channelLayout.length
    return {
        width,
        height,
        channels,
        displayName: `${revision.textureSet.legacyId} - ${revision.name ?? "Untitled"} - ${label}`,
        previewUrl: thumbnail?.thumbnail?.downloadUrl,
        textureMap,
        // the same paged loading as the editor's own source image (see OperatorStack.setSourceImage)
        createNode: (bag, id) => bag.getNode(id, ImageOpNodeDataObject, {dataObjectId, downloadFormat: "tiles", channels}),
        dispose: () => {},
    }
}

// Runs a reference image through the same Lab-space mean/variance computation the recolor operator
// uses for its own sourceStats (see compute-source-lab-stats.ts and lab-stats-subgraph.ts), entirely
// in a throwaway ImageOpNodeBag that never touches the operator-stack pipeline. Without a region this
// covers the entire image. A region must be an integer, in-bounds box (see reference-region-geometry.ts).
export async function computeTargetStatsFromReferenceImage(
    imageOpSystem: ImageOpSystem,
    image: RecolorReferenceImage,
    region?: Box2Like,
): Promise<TextureEditNodes.OperatorRecolor["targetStats"]> {
    const bag = new ImageOpNodeBag("recolorTargetStatsFromReferenceImage")
    bag.beginFrame()
    const referenceImage = image.createNode(bag, "referenceImage")
    const statsSource = region ? bag.getNode("referenceRegion", ImageOpNodeExtractRegion, {sourceImage: referenceImage, region}) : referenceImage
    bag.endFrame()

    try {
        // the native upload and the data object's tile loader already perform the sRGB -> linear
        // conversion on the GPU, and decoded EXR data is natively linear — either way the reference
        // image is "linear" downstream, same as a normal sourceImage
        return await computeLabStats(imageOpSystem, statsSource, "linear", image.channels === 1)
    } finally {
        bag.dispose()
    }
}

function createPreviewCanvas(width: number, height: number): {canvas: HTMLCanvasElement; context: CanvasRenderingContext2D} {
    const scale = Math.min(1, MAX_PREVIEW_SIZE / Math.max(width, height))
    const canvas = document.createElement("canvas")
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))
    const context = canvas.getContext("2d")
    if (!context) {
        throw new Error("Failed to get 2D canvas context")
    }
    return {canvas, context}
}

function canvasToObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(URL.createObjectURL(blob)) : reject(new Error("Failed to encode the preview image"))))
    })
}

function createPreviewUrlFromBitmap(bitmap: ImageBitmap): Promise<string> {
    const {canvas, context} = createPreviewCanvas(bitmap.width, bitmap.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    return canvasToObjectUrl(canvas)
}

// Only for display in the panel, which is why simple point sampling is good enough here. Statistics
// never come from this, they always use all of the image's pixels.
function createPreviewUrlFromPixelData(image: {width: number; height: number; channels: number; data: Float32Array}): Promise<string> {
    const {width, height, channels, data} = image
    const {canvas, context} = createPreviewCanvas(width, height)
    const preview = context.createImageData(canvas.width, canvas.height)
    for (let y = 0; y < canvas.height; y++) {
        const sourceY = Math.floor(((y + 0.5) * height) / canvas.height)
        for (let x = 0; x < canvas.width; x++) {
            const sourceX = Math.floor(((x + 0.5) * width) / canvas.width)
            const source = (sourceY * width + sourceX) * channels
            const target = (y * canvas.width + x) * 4
            const isColor = channels >= 3
            preview.data[target] = linearToSrgb(data[source]) * 255
            preview.data[target + 1] = linearToSrgb(data[source + (isColor ? 1 : 0)]) * 255
            preview.data[target + 2] = linearToSrgb(data[source + (isColor ? 2 : 0)]) * 255
            preview.data[target + 3] = 255
        }
    }
    context.putImageData(preview, 0, 0)
    return canvasToObjectUrl(canvas)
}
